import {
    faAlignCenter,
    faAlignJustify,
    faAlignLeft,
    faAlignRight,
    faBold,
    faH1,
    faH2,
    faH3,
    faH4,
    faH5,
    faItalic,
    faLink,
    faListOl,
    faListUl,
    faUnderline,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Toolbar } from '@sharedComponents/SlateComponent/index.jsx';
import { useModal } from '@sharedContexts/ModalContext';
import isHotkey from 'is-hotkey';
import { isEqual } from 'lodash';
import debounce from 'lodash/debounce';
import { useCallback, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { createEditor, Editor, Element as SlateElement, Node, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, useSlate, withReact } from 'slate-react';

import LinkModal from './LinkModal';
import { getActiveLink, isValidLinkUrl, unwrapLink, upsertLink, withInlines } from './links';
import { deserializeInitialValue, serialize, stringify } from './utils';

import * as style from './style.module.css';

const HOTKEYS = {
    'mod+b': 'bold',
    'mod+i': 'italic',
    'mod+u': 'underline',
    'mod+`': 'code',
};

const iconMap = {
    format_bold: faBold,
    format_italic: faItalic,
    format_underlined: faUnderline,
    format_list_bulleted: faListUl,
    format_list_numbered: faListOl,
    format_align_left: faAlignLeft,
    format_align_center: faAlignCenter,
    format_align_right: faAlignRight,
    format_align_justify: faAlignJustify,
    looks_one: faH1,
    looks_two: faH2,
    looks_three: faH3,
    looks_four: faH4,
    looks_five: faH5,
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

const HeadingSelect = withTranslation()(({ t }) => {
    const editor = useSlate();

    const handleChange = (event) => {
        const selectedHeading = event.target.value;
        toggleBlock(editor, selectedHeading);
    };

    const currentActiveHeadingType = useMemo(() => {
        const headingFormats = ['paragraph', 'heading-one', 'heading-two', 'heading-three', 'heading-four', 'heading-five'];
        let activeFormat = 'paragraph';

        for (const format of headingFormats) {
            if (isBlockActive(editor, format, 'type')) {
                activeFormat = format;
                break;
            }
        }
        return activeFormat;
    }, [editor.selection, editor]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <select onChange={handleChange} value={currentActiveHeadingType} className={style.selectHeading}>
            <option value="paragraph">{t('Paragraph')}</option>
            <option value="heading-one">{t('Heading 1')}</option>
            <option value="heading-two">{t('Heading 2')}</option>
            <option value="heading-three">{t('Heading 3')}</option>
            <option value="heading-four">{t('Heading 4')}</option>
            <option value="heading-five">{t('Heading 5')}</option>
        </select>
    );
});

const debouncedOnChange = debounce((newValue, onChange) => {
    onChange(newValue);
}, 2000);

const SlateEditor = ({
    initialValue = '',
    placeholder = '',
    onChangeRaw = () => {},
    onChangeHTML = () => {},
    onBeforePaste = () => true,
    maxLength = 10000,
    autoFocus = true,
    withLinks = false,
}) => {
    const renderElement = useCallback((props) => <Element {...props} />, []);
    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
    const editor = useMemo(() => withInlines(withHistory(withReact(createEditor()))), []);
    const MAX_LENGTH = maxLength;
    return (
        <Slate
            editor={editor}
            initialValue={initialValue ? deserializeInitialValue(initialValue) : [{ type: 'paragraph', children: [{ text: '' }] }]}
            onChange={(newValue) => {
                if (MAX_LENGTH !== null && newValue.map(stringify).join('').length > MAX_LENGTH) return;
                const newSerializedValue = newValue.map((n) => serialize(n)).join('');

                //initialValue is already serialized
                if (!isEqual(deserializeInitialValue(initialValue).map(stringify), newValue.map(stringify))) {
                    onChangeRaw({ serialize: newSerializedValue, raw: newValue.map(Node.string).join('') });
                    debouncedOnChange(newSerializedValue, onChangeHTML);
                }
            }}
        >
            <Toolbar className={style.toolbar}>
                <MarkButton format="bold" icon="format_bold" />
                <MarkButton format="italic" icon="format_italic" />
                <MarkButton format="underline" icon="format_underlined" />
                <HeadingSelect />
                <BlockButton format="left" icon="format_align_left" />
                <BlockButton format="center" icon="format_align_center" />
                <BlockButton format="right" icon="format_align_right" />
                <BlockButton format="justify" icon="format_align_justify" />
                <BlockButton format="bulleted-list" icon="format_list_bulleted" />
                <BlockButton format="numbered-list" icon="format_list_numbered" />
                {withLinks && <LinkButton />}
            </Toolbar>
            <Editable
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                renderPlaceholder={({ children, ...props }) => (
                    <span {...props.attributes} className={style.placeholder}>
                        {children}
                    </span>
                )}
                placeholder={placeholder}
                className={style.editor}
                style={{ minHeight: '200px' }}
                spellCheck
                /* eslint-disable-next-line jsx-a11y/no-autofocus */
                autoFocus={autoFocus}
                onDOMBeforeInput={(event) => {
                    const inputType = event.inputType;
                    if (inputType === 'insertText') {
                        const textLength = Editor.string(editor, []).length;
                        if (MAX_LENGTH !== null && textLength >= MAX_LENGTH) {
                            event.preventDefault();
                        }
                    }
                }}
                onPaste={(event) => {
                    const pasteText = event.clipboardData.getData('text/plain');
                    if (pasteText && onBeforePaste) {
                        const shouldAllowPaste = onBeforePaste(pasteText);
                        if (!shouldAllowPaste) {
                            event.preventDefault();
                            return;
                        }
                    }

                    //IF WE DON'T WANT TOAST ON PASTE LIMIT, WE CAN JUST UNCOMMENT THIS
                    // Check if paste would exceed max length
                    // const currentText = Editor.string(editor, []);
                    // if (currentText.length + pasteText.length > MAX_LENGTH) {
                    //     event.preventDefault();
                    // }
                }}
                onKeyDown={(event) => {
                    for (const hotkey in HOTKEYS) {
                        if (isHotkey(hotkey, event)) {
                            event.preventDefault();
                            const mark = HOTKEYS[hotkey];
                            toggleMark(editor, mark);
                        }
                    }
                }}
            />
        </Slate>
    );
};

const toggleBlock = (editor, format) => {
    const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type');
    const isList = LIST_TYPES.includes(format);

    Transforms.unwrapNodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_TYPES.includes(n.type) && !TEXT_ALIGN_TYPES.includes(format),
        split: true,
    });
    let newProperties;
    if (TEXT_ALIGN_TYPES.includes(format)) {
        newProperties = {
            align: isActive ? undefined : format,
        };
    } else {
        newProperties = {
            type: isActive ? 'paragraph' : isList ? 'list-item' : format,
        };
    }
    Transforms.setNodes(editor, newProperties);

    if (!isActive && isList) {
        const block = { type: format, children: [] };
        Transforms.wrapNodes(editor, block);
    }
};

const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
        Editor.removeMark(editor, format);
    } else {
        Editor.addMark(editor, format, true);
    }
};

const isBlockActive = (editor, format, blockType = 'type') => {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Array.from(
        Editor.nodes(editor, {
            at: Editor.unhangRange(editor, selection),
            match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format,
        }),
    );
    return !!match;
};

const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};

const Element = ({ attributes, children, element }) => {
    const style = { textAlign: element.align };
    switch (element.type) {
        case 'block-quote':
            return (
                <blockquote style={style} {...attributes}>
                    {children}
                </blockquote>
            );
        case 'bulleted-list':
            return (
                <ul style={style} {...attributes}>
                    {children}
                </ul>
            );
        case 'paragraph':
            return (
                <p style={style} {...attributes}>
                    {children}
                </p>
            );
        case 'heading-one':
            return (
                <h1 style={style} {...attributes}>
                    {children}
                </h1>
            );
        case 'heading-two':
            return (
                <h2 style={style} {...attributes}>
                    {children}
                </h2>
            );
        case 'heading-three':
            return (
                <h3 style={style} {...attributes}>
                    {children}
                </h3>
            );
        case 'heading-four':
            return (
                <h4 style={style} {...attributes}>
                    {children}
                </h4>
            );
        case 'heading-five':
            return (
                <h5 style={style} {...attributes}>
                    {children}
                </h5>
            );
        case 'align-left':
            return <div style={{ textAlign: 'left' }}>{children}</div>;
        case 'align-center':
            return <div style={{ textAlign: 'center' }}>{children}</div>;
        case 'align-right':
            return <div style={{ textAlign: 'right' }}>{children}</div>;
        case 'align-justify':
            return <div style={{ textAlign: 'justify' }}>{children}</div>;
        case 'list-item':
            return (
                <li style={style} {...attributes}>
                    {children}
                </li>
            );
        case 'numbered-list':
            return (
                <ol style={style} {...attributes}>
                    {children}
                </ol>
            );
        case 'link':
            return (
                <a href={isValidLinkUrl(element.url ?? '') ? element.url : undefined} {...attributes}>
                    {children}
                </a>
            );

        default:
            return (
                <p style={style} {...attributes}>
                    {children}
                </p>
            );
    }
};

const Leaf = ({ attributes, children, leaf }) => {
    let output = children;
    if (leaf.bold) {
        output = <strong>{output}</strong>;
    }
    if (leaf.code) {
        output = <code>{output}</code>;
    }
    if (leaf.italic) {
        output = <em>{output}</em>;
    }
    if (leaf.underline) {
        output = <u>{output}</u>;
    }
    return <span {...attributes}>{output}</span>;
};

const BlockButton = ({ format, icon }) => {
    const editor = useSlate();
    const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type');
    return (
        <div className={`${style.wrappedButton} ${isActive ? style.active : ''}`}>
            <Button
                active={isActive}
                onMouseDown={(event) => {
                    event.preventDefault();
                    toggleBlock(editor, format);
                }}
            >
                <FontAwesomeIcon icon={iconMap[icon]} />
            </Button>
        </div>
    );
};

const MarkButton = ({ format, icon }) => {
    const editor = useSlate();
    const isActive = isMarkActive(editor, format);
    return (
        <div className={`${style.wrappedButton} ${isActive ? style.active : ''}`}>
            <Button
                active={isActive}
                onMouseDown={(event) => {
                    event.preventDefault();
                    toggleMark(editor, format);
                }}
            >
                <FontAwesomeIcon icon={iconMap[icon]} />
            </Button>
        </div>
    );
};

const LinkButton = () => {
    const editor = useSlate();
    const { t } = useTranslation();
    const { actions: modalActions } = useModal();
    const isActive = Boolean(getActiveLink(editor));

    // The dialog takes the focus: the selection is saved first, then restored before the link is applied.
    const openLinkModal = () => {
        const { selection } = editor;
        const activeLink = getActiveLink(editor);
        const restoreSelection = () => {
            Transforms.select(editor, selection ?? Editor.end(editor, []));
            ReactEditor.focus(editor);
        };
        modalActions.open({
            component: LinkModal,
            title: activeLink ? t('Edit the link') : t('Insert a link'),
            size: 's',
            initialUrl: activeLink?.url ?? '',
            canRemove: Boolean(activeLink),
            onCancel: () => modalActions.close(),
            onSubmit: (url) => {
                modalActions.close();
                restoreSelection();
                upsertLink(editor, url);
            },
            onRemove: () => {
                modalActions.close();
                restoreSelection();
                unwrapLink(editor);
            },
        });
    };

    return (
        <div className={`${style.wrappedButton} ${isActive ? style.active : ''}`}>
            <Button
                active={isActive}
                role="button"
                tabIndex={0}
                aria-label={t('Link')}
                onMouseDown={(event) => {
                    event.preventDefault();
                    openLinkModal();
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openLinkModal();
                    }
                }}
            >
                <FontAwesomeIcon icon={faLink} />
            </Button>
        </div>
    );
};

export default SlateEditor;
